// Imports => React
import { useMemo } from 'react';
import { withStore } from '@stores';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';

// Imports => Assets
import TransactionHero1x from '@assets/images/transaction-hero-1x.png';

import {
  AcIsArray,
  DATETIME_FORMATS,
  KEYS,
  THEMES,
} from '@opp/ui-kit';

// Imports => Utilities
import {
  AcIsSet,
  AcUUID,
  AcSanitize,
} from '@opp/ui-kit';
import {
  AcFormatCurrencyAmount,
  AcFormatDate,
} from '@utils';

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcHeading,
  AcRichContent,
  AcCard,
  AcEmptyBlock,
  AcImage,
  AcBadge,
  AcDefinitionList,
} from '@opp/ui-kit';
import {withTranslation} from "react-i18next";
import AcCopyText from "../../../atoms/ac-copy-text/ac-copy-text";
import {AcFormatPaymentMethod, AcGetPaymentMethodIcon, AcGetPaymentMethodImg} from "../../../utilities/ac-format-payment-method";
import {AcGetDateIsInFuture} from "../../../utilities/ac-format-date";
import moment from "moment/moment";

const _CLASSES = {
  MAIN: 'ac-purchase-details-details-tab ac-purchase__grid-container',
  PAYMENT_ICON: 'ac-purchase__payment-icon',
};

const AcPurchaseDetailsDetailsTab = ({ store: { profile }, data, t, i18n }) => {

  const { current_partner_currency, current_merchant_profiles } = profile;

  const getPaymentIconClassNames = useMemo(() => {
    return clsx(_CLASSES.PAYMENT_ICON);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  });

  const getPartnerNameBadge = useMemo(() => {
    if (!AcIsSet(current_merchant_profiles) || !AcIsSet(current_merchant_profiles.partner)) {
      return null;
    }

    return (
      <div className={'h-margin-top-5'}>
        <AcBadge theme={THEMES.OMEGA_LIGHT}>
          <span className={'h-text--color-pitch'}>{current_merchant_profiles.partner.name}</span>
        </AcBadge>
      </div>
    );
  }, [current_merchant_profiles]);

  const getPaymentMethodIcon = useMemo(() => {
    if (!AcIsSet(data) || (!AcIsSet(data.payment_method) && !AcIsSet(data.parent_payment_method))) {
      return null;
    }

    return AcGetPaymentMethodImg(t, getPaymentIconClassNames, data.payment_method, data.parent_payment_method);
  }, [data]);

  const getStatusBadge = useMemo(() => {
    if (!AcIsSet(data)) return null;

    const { status } = data;

    let label = t(`status.${status}`, {ns: 'common'});
    let theme = null;

    switch (status) {
      case 'created':
      case 'reserved':
      case 'pending':
      case 'planned':
        theme = THEMES.OMEGA_LIGHT;
        break;

      case 'on_hold':
      case 'refunded':
        theme = THEMES.ECHO;
        break;

      case 'chargeback':
        theme = THEMES.BETA_LIGHT;
        break;

      case 'expired':
      case 'cancelled':
      case 'failed':
      case 'revoked':
        theme = THEMES.ERROR;
        break;

      case 'completed':
        theme = THEMES.SUCCESS;
        break;

      default:
    }

    return <AcBadge theme={theme}>{label}</AcBadge>;
  }, [data]);

  const renderMainFeatures = useMemo(() => {
    const { uid, amount, order } = data;
    // Todo currency
    const content = AcFormatCurrencyAmount(amount, current_partner_currency, i18n.resolvedLanguage, true);

    return (
      <AcContainer flex className={'ac-purchase__container'}>
        <AcRow className={'h-margin-bottom-auto'}>
          <AcColumn xs={6} sm={6} md={6}>
            <AcHeading
              rank={2}
              theme={THEMES.DARK}
              className={'ac-purchase__amount'}
            >
              {content}
            </AcHeading>
          </AcColumn>

          <AcColumn
            xs={6}
            sm={6}
            md={6}
            className={'h-text--align-right h-flex-h-align-end'}
          >
            {getPartnerNameBadge}
            {getPaymentMethodIcon}
          </AcColumn>

          {AcIsSet(order) && AcIsSet(order.products) && AcIsArray(order.products) && AcIsSet(order.products[0]) &&
                        <AcColumn className={'h-hidden-xxs h-hidden-xs h-hidden-sm'}>
                          <AcRichContent content={`<span class="h-text--color-medium h-text--size-medium">${AcSanitize(order.products[0].name)}</span>`} />
                        </AcColumn>
          }
        </AcRow>
      </AcContainer>
    );
  }, [getPaymentMethodIcon, data, i18n.resolvedLanguage, current_partner_currency]);

  const renderStatus = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.status))
      return (
        <AcCard className={'ac-purchase__column'}>
          <AcRichContent
            content={`<p><em>${t('no-status-information', 'No status information available', {ns: 'purchases'})}</em></p>`}
          />
        </AcCard>
      );

    const { created, date_paid, status, escrow, category, uid } = data;

    const date_format =
            window.outerWidth <= 768
              ? DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES_SHORT
              : DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES;

    let label = t('details.date-paid', 'Date paid', {ns: 'purchases'});
    if (date_paid) {
      label = AcGetDateIsInFuture(moment(date_paid * 1000)) ? t('details.date-planned', 'Date planned', {ns: 'purchases'}) : t('details.date-paid', 'Date paid', {ns: 'purchases'})
    }

    const result = {
      status: [{ label: `${t('details.status', 'Status', {ns: 'purchases'})}`, value: getStatusBadge }],
      details: [
        {
          label: `${t('created-at', 'Created at', {ns: 'common'})}`,
          value: AcFormatDate(created * 1000, null, date_format),
        },
        {
          label: label,
          value: date_paid ? AcFormatDate(date_paid * 1000, null, date_format) : t('details.not-paid-yet', 'Not paid yet.', {ns: 'purchases'}),
        },
      ],
    };

    if (
      category &&
            category === KEYS.ESCROW &&
            AcIsSet(escrow.end) &&
            status === KEYS.RESERVED
    ) {
      result.details.push({
        label: `${t('details.reserved-until', 'Reserved until', {ns: 'purchases'})}`,
        value: AcFormatDate(escrow.end * 1000, null, date_format),
      });
    }

    if (AcIsSet(uid)) {
      result.status.unshift({
        label: `${t('uid', 'Unique identifier', {ns: 'common'})}`,
        value: <AcCopyText
          t={t}
          value={uid}
          title={t('copy-uid', 'Copy UID to clipboard', {ns: 'common'})}
          id={'ac-tra-uid'}
          fixed
        >
          {uid}
        </AcCopyText>,
      });
    }

    return (
      <AcCard className={'ac-purchase__column'}>
        <AcHeading theme={THEMES.PITCH} rank={5} tag={'h3'}>
          {t('details', 'Details', {ns: 'common'})}
        </AcHeading>
        <AcDefinitionList items={result.status} />
        <AcDefinitionList items={result.details} />
      </AcCard>
    );
  }, [data, getStatusBadge]);

  const renderProducts = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.order))
      return (
        <AcEmptyBlock message={t('no-products-available', 'No product details available', {ns: 'purchases'})} border={false} />
      );

    const collection = data.order.products;
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const item = collection[n];

      const price = AcFormatCurrencyAmount((item.price.amount * item.quantity), item.price.currency, i18n.resolvedLanguage, true);

      let label = item.quantity === 1 ? '' : item.quantity + 'x ';
      label += AcSanitize(item.name);
      const object = {
        label: label,
        value: price,
      };

      result.push(object);
    }

    return <AcDefinitionList items={result} />;
  }, [data, i18n.resolvedLanguage]);

  const renderImage = useMemo(() => {
    return (
      <AcImage
        wrpClassName={'ac-transaction__hero'}
        source={TransactionHero1x}
        key={AcUUID()}
        transparent
      />
    )
  }, []);

  return (
    <div className={getMainClassNames}>
      <AcCard className={'ac-purchase__column ac-purchase__column--big'}>
        {renderImage}
        {renderMainFeatures}
      </AcCard>

      {renderStatus}

      <AcCard className={'ac-purchase__column'}>
        <AcHeading theme={THEMES.PITCH} rank={5} tag={'h3'}>
          {t('products', 'Products', {ns: 'purchases'})}
        </AcHeading>
        {renderProducts}
      </AcCard>
    </div>
  );
};

export default withTranslation(['common', 'purchases'])(withStore(observer(AcPurchaseDetailsDetailsTab)));
